import { AddOutlined, FormatListBulleted } from '@mui/icons-material'
import { Box, Button, Stack, TablePagination } from '@mui/material'
import { FC, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Header } from 'src/components/header/Header'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import ExcelConversionFlowList from 'src/features/excelConversionFlows/ExcelConversionFlowList'
import { useLimitedFeatureAvailability } from 'src/features/featureAvailability/useLimitedFeatureAvailability'
import {
  changeExcelConversionFlowsPage as changeExcelConversionFlowsPageAction,
  changeExcelConversionFlowsSize as changeExcelConversionFlowsSizeAction,
  getExcelConversionFlows as getExcelConversionFlowsAciton,
} from 'src/state/ducks/excelConversionFlows/actions'
import { RootState } from 'src/state/store'
import Loading from '../../components/loading/Loading'

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100]

const ExcelConversionFlowsContainer: FC = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  const { isFeatureAvailable } = useLimitedFeatureAvailability()

  const changeFlowsPage = (page: number, pageSize?: number) => {
    dispatch(changeExcelConversionFlowsPageAction(page, pageSize))
  }

  const changeFlowsSize = (pageSize: number) => {
    dispatch(changeExcelConversionFlowsSizeAction(pageSize))
  }

  const goAddFlow = () => {
    history.push('/excelConversionFlows/add')
  }

  const goLogs = () => {
    history.push('/excelConversionLogs')
  }

  const { excelConversionFlows, isLoading, count, request } = useSelector(
    (state: RootState) => state.excelConversionFlowsState.excelConversionFlows,
  )

  useEffect(() => {
    if (!ROWS_PER_PAGE_OPTIONS.includes(request.limit ?? 25)) {
      dispatch(changeExcelConversionFlowsSizeAction(25))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.limit])

  useEffect(() => {
    dispatch(
      getExcelConversionFlowsAciton({
        limit: request.limit ?? 25,
        offset: request.offset ?? 0,
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ⚠️ changeExcelConversionFlowsPage の第一引数は 1 オリジン
  // MUI の TablePagination は 0 オリジン
  const currentPage =
    request.offset === undefined ||
    request.limit === undefined ||
    request.limit === 0
      ? 0
      : request.offset / request.limit

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    changeFlowsPage(newPage + 1, request.limit)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const size = parseInt(event.target.value, 10)
    changeFlowsSize(size)
  }

  return (
    <Stack>
      <Header>
        <HeaderTitle title="Excel変換" />
      </Header>
      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        sx={{ p: 2 }}
      >
        <Button
          variant="outlined"
          startIcon={<FormatListBulleted />}
          onClick={goLogs}
        >
          ダウンロードファイル一覧
        </Button>
        {isFeatureAvailable('excelConversionEdit') && (
          <Button
            color="primary"
            variant="contained"
            startIcon={<AddOutlined />}
            onClick={goAddFlow}
          >
            変換フローを追加
          </Button>
        )}
      </Stack>
      <Box sx={{ flexGrow: 1, overflow: 'auto', position: 'relative' }}>
        {isLoading ? (
          <Loading />
        ) : (
          <ExcelConversionFlowList flows={excelConversionFlows} />
        )}
      </Box>
      <TablePagination
        component="div"
        count={count}
        page={currentPage}
        onPageChange={handleChangePage}
        rowsPerPage={
          ROWS_PER_PAGE_OPTIONS.includes(request.limit ?? 25)
            ? (request.limit ?? 25)
            : 25
        }
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        labelRowsPerPage="1ページあたり:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}〜${to} (${count !== -1 ? count : '不明'} 件中)`
        }
        sx={{
          '& .MuiTablePagination-selectLabel': {
            margin: 0,
          },
          '& .MuiTablePagination-displayedRows': {
            margin: 0,
          },
        }}
      />
    </Stack>
  )
}

export default ExcelConversionFlowsContainer
